// Performance optimization utilities

/**
 * Detect if device prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Check if device is low-end based on various metrics
 */
export const isLowEndDevice = (): boolean => {
  // Check hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 1;
  
  // Check device memory (if available)
  const memory = (navigator as any).deviceMemory || 4;
  
  // Check connection type (if available)
  const connection = (navigator as any).connection;
  const effectiveType = connection?.effectiveType || '4g';
  
  return cores <= 2 || memory <= 2 || effectiveType === 'slow-2g' || effectiveType === '2g';
};

/**
 * Get optimal animation duration based on device capability
 */
export const getAnimationDuration = (baseMs: number): number => {
  if (prefersReducedMotion()) return 0;
  if (isLowEndDevice()) return baseMs * 0.5; // Reduce duration on low-end devices
  return baseMs;
};

/**
 * Request idle callback wrapper with fallback
 */
export const requestIdleCallback = (callback: () => void, options?: { timeout?: number }) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, options);
  }
  return setTimeout(callback, 1);
};

/**
 * Cancel idle callback wrapper with fallback
 */
export const cancelIdleCallback = (id: number) => {
  if ('cancelIdleCallback' in window) {
    return window.cancelIdleCallback(id);
  }
  return clearTimeout(id);
};

/**
 * Measure component render time (dev only)
 */
export const measureRender = (componentName: string, callback: () => void) => {
  if (process.env.NODE_ENV !== 'development') {
    callback();
    return;
  }

  const start = performance.now();
  callback();
  const end = performance.now();
  
  console.log(`[Performance] ${componentName} rendered in ${(end - start).toFixed(2)}ms`);
};

/**
 * Preload critical resources
 */
export const preloadResource = (href: string, as: string, type?: string) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (type) link.type = type;
  document.head.appendChild(link);
};

/**
 * Lazy load a module with error handling
 */
export const lazyLoadModule = async <T>(
  importFn: () => Promise<T>,
  fallback?: T
): Promise<T> => {
  try {
    return await importFn();
  } catch (error) {
    console.error('Failed to lazy load module:', error);
    if (fallback) return fallback;
    throw error;
  }
};

/**
 * Add will-change hint to element
 */
export const addWillChange = (element: HTMLElement, properties: string[]) => {
  element.style.willChange = properties.join(', ');
  
  // Remove after animation completes (prevent memory issues)
  const removeWillChange = () => {
    element.style.willChange = 'auto';
    element.removeEventListener('transitionend', removeWillChange);
    element.removeEventListener('animationend', removeWillChange);
  };
  
  element.addEventListener('transitionend', removeWillChange);
  element.addEventListener('animationend', removeWillChange);
};

/**
 * Check if browser supports WebP
 */
export const supportsWebP = async (): Promise<boolean> => {
  if (!('createImageBitmap' in window)) return false;
  
  const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
  const blob = await fetch(webpData).then(r => r.blob());
  
  try {
    await createImageBitmap(blob);
    return true;
  } catch {
    return false;
  }
};
